
import { useEffect, useState } from 'react';
import { profileApi } from '@/lib/api/auth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { SettingsFormSkeleton } from '@/components/ui/skeletons';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/page-header';

function NotificationsPage() {
  usePageTitle('Notifications');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [taskRemindersEnabled, setTaskRemindersEnabled] = useState(false);

  useEffect(() => {
    profileApi.get().then((profile) => {
      setTaskRemindersEnabled(profile.taskRemindersEnabled ?? false);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, []);

  async function handleToggle(checked: boolean) {
    setTaskRemindersEnabled(checked);
    setIsSaving(true);
    try {
      await profileApi.updateNotifications({ taskRemindersEnabled: checked });
      toast.success(checked ? 'Task reminders enabled.' : 'Task reminders disabled.');
    } catch {
      setTaskRemindersEnabled(!checked);
      toast.error('Failed to update notification settings.');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <SettingsFormSkeleton rows={2} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        askAi
        description="Manage how and when you receive email notifications."
        hideTitleOnMobile={false}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Task reminders</CardTitle>
          <CardDescription>
            Receive an email 24 hours before a task is due and again when it becomes due.
            Only tasks assigned to you are included.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Switch
              id="task-reminders"
              checked={taskRemindersEnabled}
              onCheckedChange={handleToggle}
              disabled={isSaving}
            />
            <Label htmlFor="task-reminders" className="cursor-pointer">
              {taskRemindersEnabled ? 'Enabled' : 'Disabled'}
            </Label>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/notifications')({ component: NotificationsPage });
